import { FC, useCallback, useMemo, useRef } from 'react';
import Text, { TextPresets } from '@stripcash/ui-kit/components/Text';
import Button, {
  ButtonColor,
  ButtonVariant,
} from '@stripcash/ui-kit/components/Button';
import cn from 'classnames';
import TextArea from '@stripcash/ui-kit/components/TextArea';
import ProgressBar from '@stripcash/ui-kit/components/ProgressBar';
import { useTheme } from '@stripcash/ui-kit/hoc/Theme';

import { useT } from 'common/i18n';
import useCloseOutside from 'common/components/SidePanel/utils/useCloseOutside';
import { useGlobalApi } from 'common/apps/core/ApiConsumer';
import {
  Survey,
  SurveyButtonType,
  SurveyDataProps,
  SurveyDataType,
} from 'common/apps/modules/survey/types';
import rootSurveySelector from 'common/apps/modules/survey/selectors/rootSurveySelector';

import SmilesGroupSwitch from '../../../SmilesGroupSwitch';
import CloseModalStep from '../CloseModalStep';

import s from './styles.module.css';

type CurrentApi = {
  survey: Survey.ModuleApi;
};

type SurveyQuestionsModalContainerProps = {
  surveyData: SurveyDataProps;
  onClose: () => void;
} & Omit<ReturnType<typeof rootSurveySelector>, 'surveyData'>;

const SurveyQuestionsModalContainer: FC<SurveyQuestionsModalContainerProps> = ({
  surveyData,
  activeStep,
  isSurveySubmitted,
  onClose,
}) => {
  const {
    survey: { handleAnswer, handleChangeStep },
  } = useGlobalApi<CurrentApi>();
  const t = useT('questionnaire');

  const { currentTheme } = useTheme();

  const mainModalRef = useRef(null);
  const closeModalRef = useRef<HTMLDivElement>(null);

  const activeStepData = useMemo(
    () => surveyData.questions.find((_, index) => index + 1 === activeStep)!,
    [activeStep, surveyData],
  );

  const isStepOptional = useMemo(
    () =>
      activeStepData?.uuid ===
      surveyData.questions[surveyData.questions.length - 1].uuid,
    [activeStepData?.uuid, surveyData.questions],
  );

  const handleChangeRate = useCallback(
    (value) => {
      handleAnswer({ id: activeStepData.uuid, answer: String(value) });
    },
    [activeStepData.uuid, handleAnswer],
  );

  useCloseOutside({
    refs: [mainModalRef, closeModalRef],
    isOpen: true,
    setOpen: onClose,
  });

  return (
    <>
      {!isSurveySubmitted && (
        <div ref={mainModalRef} className={s.surveyModal}>
          <ProgressBar
            length={surveyData.questions.length}
            active={activeStep}
          />

          <div className={s.surveyModalTitle}>
            <Text>
              {t('surveyModal.title')} {activeStep}{' '}
              {isStepOptional && t('surveyModal.optionalTitle')}
            </Text>
          </div>

          <Text preset={TextPresets.H3}>{t(activeStepData.title)}</Text>

          <div className={s.surveyModalRespond}>
            {activeStepData?.type === SurveyDataType.Rate && (
              <SmilesGroupSwitch
                t={t.rebase('surveyModal.smiles')}
                onSelect={handleChangeRate}
                value={Number(activeStepData?.answer) || 0}
              />
            )}

            {activeStepData?.type === SurveyDataType.Text && (
              <div className={s.surveyModalRespondTextarea}>
                <TextArea
                  value={activeStepData.answer}
                  onChange={handleChangeRate}
                  maxLength={1000}
                  palette={currentTheme}
                  placeholder={t('surveyModal.placeholder') as never}
                />
              </div>
            )}
          </div>

          <div
            className={cn(s.surveyModalButtons, {
              [s.hideButtonPrev]: activeStep === 1,
            })}
          >
            <Button
              id={SurveyButtonType.Prev}
              color={ButtonColor.Default}
              variant={ButtonVariant.Secondary}
              onClick={handleChangeStep}
            >
              {t('surveyModal.prevBtn')}
            </Button>

            <Button
              id={SurveyButtonType.Next}
              onClick={handleChangeStep}
              color={ButtonColor.Default}
              disabled={
                activeStepData.required
                  ? !('answer' in activeStepData) ||
                    activeStepData.answer === ''
                  : false
              }
            >
              {activeStep === surveyData.questions.length
                ? t('surveyModal.submit')
                : t('surveyModal.nextBtn')}
            </Button>
          </div>
        </div>
      )}

      {isSurveySubmitted && (
        <CloseModalStep
          ref={closeModalRef}
          onClose={onClose}
          t={t.rebase('surveyModal')}
        />
      )}
    </>
  );
};

export default SurveyQuestionsModalContainer;
